import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useFrequentPassengersContext from '../context/FrequentPassengers';
import useLoyaltyPrograms from '../loyalty/context/useLoyaltyPrograms';

const normalizeName = (value) => value?.trim().toLowerCase() || '';

const passengersHaveSameName = (firstPassenger, secondPassenger) =>
  Boolean(firstPassenger?.firstName && firstPassenger?.lastName) &&
  normalizeName(firstPassenger.firstName) === normalizeName(secondPassenger?.firstName) &&
  normalizeName(firstPassenger.lastName) === normalizeName(secondPassenger?.lastName);

/**
 * Custom hook to filter frequent passengers excluding the Doters passenger
 * when logged in and not the first passenger, and excluding all passengers
 * when in an exchange process
 * @param {number} passengerIndex - The current passenger index
 * @returns {Array} Filtered array of frequent passengers
 */
const useFilteredFrequentPassengers = (passengerIndex) => {
  const { frequentPassengers } = useFrequentPassengersContext();
  const { doters } = useLoyaltyPrograms();
  const isExchange = useSelector((state) => state.purchase.get('isExchange'));
  const firstFormPassenger = useSelector(
    (state) => state.form?.passengers?.values?.passengers?.[0],
  );

  const filteredFrequentPassengers = useMemo(() => {
    // Handle null/undefined frequentPassengers
    if (!frequentPassengers) {
      return frequentPassengers;
    }

    // Convert passengerIndex to number for comparison
    const numericPassengerIndex = Number(passengerIndex);

    // If we're in an exchange process, don't allow any frequent passenger selection
    // This ensures the ticket remains tied to the original passenger who bought it
    if (isExchange) {
      return [];
    }

    if (!doters?.userIsLoggedIn || numericPassengerIndex === 1) {
      return frequentPassengers;
    }

    // Find the passenger that is selected for the first position (Doters passenger)
    const selectedDotersPassenger = frequentPassengers.find((fp) => fp.selectedForPassenger === 1);
    const accountIsFirstPassenger = passengersHaveSameName(firstFormPassenger, doters.profile);
    const dotersPassenger =
      selectedDotersPassenger ||
      (accountIsFirstPassenger
        ? frequentPassengers.find((passenger) => passengersHaveSameName(passenger, doters.profile))
        : null);

    if (!dotersPassenger) {
      return frequentPassengers;
    }

    // Filter out the Doters passenger from the list
    return frequentPassengers.filter((fp) => fp.id !== dotersPassenger.id);
  }, [doters, firstFormPassenger, passengerIndex, frequentPassengers, isExchange]);

  return filteredFrequentPassengers;
};

export default useFilteredFrequentPassengers;
